import { motion } from 'framer-motion';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_API_URL } from '../helpers/Config';
import toast from 'react-hot-toast';

const SwapReportPage = ({ theme = 'dark' }) => {
    const [swaps, setSwaps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [lastId, setLastId] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [count, setCount] = useState(0);
    const observerRef = useRef(null);

    const navigate = useNavigate();
    const isDarkMode = theme === 'dark';

    const pageStyles = {
        minHeight: '100vh',
        padding: '1rem 0',
        fontSize: '14px',
        color: isDarkMode ? '#e5e7eb' : '#2c3e50',
        transition: 'background 0.25s ease, color 0.25s ease',
        backgroundColor: isDarkMode ? '#020617' : '#f8f9fa'
    };

    const fetchSwapReport = useCallback(async (isLoadMore = false) => {
        try {
            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const response = await axios.post(`${BASE_API_URL}/payment/swap-report`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
                last_id: isLoadMore ? lastId : 0
            });

            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
                return;
            }

            if (data?.data) {
                if (isLoadMore) {
                    setSwaps(prev => [...prev, ...data.data]);
                } else {
                    setSwaps(data.data);
                }
                setCount(data.count || 0);
                setLastId(data.last_id || 0);
                setHasMore(data.has_more || false);
            }
        } catch (error) {
            console.log('Error fetching swap report:', error);
            if (!isLoadMore) {
                toast.error('Failed to load swap history');
            }
        } finally {
            if (isLoadMore) {
                setLoadingMore(false);
            } else {
                setLoading(false);
            }
        }
    }, [lastId, navigate]);

    // Initial load
    useEffect(() => {
        fetchSwapReport();
    }, [fetchSwapReport]);

    // Infinite scroll observer
    useEffect(() => {
        if (loading || loadingMore || !hasMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    fetchSwapReport(true);
                }
            },
            {
                root: null,
                rootMargin: '100px',
                threshold: 0.1
            }
        );

        if (observerRef.current) {
            observer.observe(observerRef.current);
        }

        return () => {
            if (observerRef.current) {
                observer.unobserve(observerRef.current);
            }
        };
    }, [loading, loadingMore, hasMore, fetchSwapReport]);

    const formatNumber = (num) => {
        const n = parseFloat(num);
        if (isNaN(n)) return '0.00';
        return n.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);

        // Format: DD/MM/YY - HH:MM AM/PM
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString().slice(-2);

        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const formattedHours = hours.toString().padStart(2, '0');

        return `${day}/${month}/${year} - ${formattedHours}:${minutes} ${ampm}`;
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const formattedHours = hours.toString().padStart(2, '0');

        return `${formattedHours}:${minutes} ${ampm}`;
    };

    const formatDateShort = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } else {
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear().toString().slice(-2);
            return `${day}/${month}/${year}`;
        }
    };

    // Group swaps by date
    const groupedSwaps = swaps.reduce((groups, swap) => {
        const date = swap.create_date.split('T')[0];
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(swap);
        return groups;
    }, {});

    const groupedArray = Object.entries(groupedSwaps).map(([date, items]) => ({
        date,
        items
    }));

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="coupon-page-container"
            style={pageStyles}
        >
            <div className="container-fluid px-3" style={{ maxWidth: '480px', margin: '0 auto' }}>
                {/* Header */}
                <div className="d-flex align-items-center mb-4">
                    <h5 className="mb-0 fw-bold">Swap History</h5>
                </div>


                {/* Content Area */}
                {loading ? (
                    // Loading skeleton
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                            Loading swap history...
                        </div>
                    </div>
                ) : swaps.length === 0 ? (
                    // Empty state
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-5"
                    >
                        <div className="mb-4" style={{ fontSize: '3rem', opacity: 0.5 }}>
                            🔄
                        </div>
                        <h6 className="mb-2">No Swap History</h6>
                        <p className="text-muted mb-4" style={{ fontSize: '0.9rem' }}>
                            You haven't made any swaps yet
                        </p>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/swap')}
                        >
                            Make Your First Swap
                        </button>
                    </motion.div>
                ) : (
                    // Swap list
                    <>
                        {groupedArray.map((group, groupIndex) => (
                            <motion.div
                                key={group.date}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: groupIndex * 0.05 }}
                                className="mb-4"
                            >
                                {/* Date header */}
                                <div className="d-flex align-items-center my-3">
                                    <div
                                        className="flex-grow-1"
                                        style={{
                                            height: '1px',
                                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                                        }}
                                    ></div>
                                    <div
                                        className="px-3 py-1 rounded-pill mx-2"
                                        style={{
                                            backgroundColor: isDarkMode ? 'rgba(15,23,42,0.9)' : 'rgba(0,0,0,0.05)',
                                            color: isDarkMode ? '#adb5bd' : '#6c757d',
                                            fontSize: '0.75rem',
                                            fontWeight: 500
                                        }}
                                    >
                                        {formatDateShort(group.date)}
                                    </div>
                                    <div
                                        className="flex-grow-1"
                                        style={{
                                            height: '1px',
                                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                                        }}
                                    ></div>
                                </div>

                                {/* Swap items */}
                                <div>
                                    {group.items.map((swap, index) => (
                                        <motion.div
                                            key={`${swap.swap_id}-${index}`}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.02 }}
                                            className="card border-0 shadow-sm mb-3"
                                            style={{
                                                backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.9)' : '#ffffff',
                                                borderRadius: '12px',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <div className="card-body p-3">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div className="d-flex align-items-center">
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                                            style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                backgroundColor: swap.type === 'usdt to coin'
                                                                    ? 'rgba(34, 197, 94, 0.15)'
                                                                    : 'rgba(59, 130, 246, 0.15)'
                                                            }}
                                                        >
                                                            <span style={{
                                                                fontSize: '1.2rem',
                                                                color: swap.type === 'usdt to coin'
                                                                    ? '#22c55e'
                                                                    : '#3b82f6'
                                                            }}>
                                                                {swap.type === 'usdt to coin' ? '🪙' : '$'}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <div
                                                                className="fw-bold mb-1"
                                                                style={{ fontSize: '0.85rem' }}
                                                            >
                                                                {swap.type === 'usdt to coin'
                                                                    ? 'USDT to Coins'
                                                                    : 'Coins to USDT'}
                                                            </div>
                                                            <div
                                                                className="text-muted"
                                                                style={{ fontSize: '0.75rem' }}
                                                            >
                                                                {formatDateTime(swap.create_date)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="text-end">
                                                        <div
                                                            className={`fw-bold ${swap.type === 'usdt to coin' ? 'text-success' : 'text-primary'}`}
                                                            style={{ fontSize: '0.9rem' }}
                                                        >
                                                            {swap.type === 'usdt to coin'
                                                                ? `+${formatNumber(swap.coin)}`
                                                                : `+$${formatNumber(swap.usdt)}`}
                                                        </div>
                                                        <div
                                                            className="text-muted"
                                                            style={{ fontSize: '0.75rem' }}
                                                        >
                                                            {swap.type === 'usdt to coin'
                                                                ? `$${formatNumber(swap.usdt)}`
                                                                : `${formatNumber(swap.coin)} coins`}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>
                        ))}

                        {/* Load more indicator */}
                        {hasMore && (
                            <div ref={observerRef} className="text-center py-4">
                                {loadingMore ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
                                            <span className="visually-hidden">Loading more...</span>
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                            Loading more swaps...
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                        Scroll down to load more
                                    </div>
                                )}
                            </div>
                        )}

                        {/* End of list message */}
                        {!hasMore && swaps.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center py-4"
                            >
                                <div
                                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        backgroundColor: isDarkMode ? 'rgba(15,23,42,0.9)' : 'rgba(0,0,0,0.05)'
                                    }}
                                >
                                    <span style={{ fontSize: '1.5rem', opacity: 0.7 }}>✓</span>
                                </div>
                                <div className="fw-bold mb-1" style={{ fontSize: '0.9rem' }}>
                                    All swaps loaded
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                    You've reached the end of your swap history
                                </div>
                            </motion.div>
                        )}
                    </>
                )}

                {/* Bottom Navigation */}
                {swaps.length > 0 && (
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="mt-4"
                    >
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-outline-primary flex-fill"
                                onClick={() => navigate(-1)}
                                style={{ fontSize: '0.85rem' }}
                            >
                                ← Go Back
                            </button>
                            <button
                                className="btn btn-outline-secondary flex-fill"
                                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                style={{ fontSize: '0.85rem' }}
                            >
                                ↑ Scroll to Top
                            </button>
                        </div>
                    </motion.div>
                )}
            </div>

            <style>{`
                .coupon-page-container {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                }
                
                .card {
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                
                .card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }
                
                .btn {
                    border-radius: 8px;
                    font-weight: 600;
                }
            `}</style>
        </motion.div>
    );
};

export default SwapReportPage;